import { useMemo, useState } from 'react'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'
import { Icon, Text, useTheme } from 'react-native-paper'
import {
    isOutsideHoursWorking,
    isWorkingStatus,
    type LiveStatus,
    MOONLIGHT_COLOR,
} from '@/constants/monitor-status'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useMonitorCardsView } from '@/stores/monitor-store'
import type { UserLocationWithStatus } from '@/types/movement'
import { MonitorLegend } from '@/ui/monitor/MonitorLegend'
import { MonitorMomentPicker } from '@/ui/monitor/MonitorMomentPicker'

interface Props {
    userLocations: UserLocationWithStatus[]
}

interface Counts {
    working: number
    working_on_site: number
    working_idle: number
    late: number
    off_hours: number
    not_tracking: number
    no_data_today: number
}

function countUsers(userLocations: UserLocationWithStatus[]): Counts {
    const counts: Counts = {
        working: 0,
        working_on_site: 0,
        working_idle: 0,
        late: 0,
        off_hours: 0,
        not_tracking: 0,
        no_data_today: 0,
    }
    for (const u of userLocations) {
        const s = u.trackingStatus.status
        if (isWorkingStatus(s)) counts.working += 1
        if (isOutsideHoursWorking(s)) counts.late += 1
        if (s === 'working_on_site') counts.working_on_site += 1
        else if (s === 'working_idle') counts.working_idle += 1
        else if (s === 'off_hours') counts.off_hours += 1
        else if (s === 'not_tracking') counts.not_tracking += 1
        else if (s === 'no_data_today') counts.no_data_today += 1
    }
    return counts
}

export function MonitorHeader({ userLocations }: Props) {
    const theme = useTheme()
    const semantic = useSemanticColors()
    const { statusFilter, setStatusFilter } = useMonitorCardsView()
    const [legendVisible, setLegendVisible] = useState(false)

    const counts = useMemo(() => countUsers(userLocations), [userLocations])

    // not_tracking users are hidden from the roster entirely, so don't include
    // them in the Off count (the chip would otherwise report more users than
    // actually appear when clicked).
    const offCount = counts.off_hours + counts.no_data_today

    const chips: {
        key: LiveStatus | 'working' | 'off' | 'late'
        label: string
        count: number
        color: string
    }[] = [
        { key: 'working', label: 'Working', count: counts.working, color: semantic.success },
        {
            key: 'working_on_site',
            label: 'On site',
            count: counts.working_on_site,
            color: semantic.success,
        },
        {
            key: 'working_idle',
            label: 'Idle',
            count: counts.working_idle,
            color: semantic.warning,
        },
        // Late/early clock-ins get their own chip — they read as "on the
        // clock but outside the schedule" which is a distinct concern from
        // both active working-hours states and fully-off team members.
        ...(counts.late > 0
            ? [
                  {
                      key: 'late' as const,
                      label: 'Late',
                      count: counts.late,
                      color: MOONLIGHT_COLOR,
                  },
              ]
            : []),
        { key: 'off', label: 'Off', count: offCount, color: semantic.neutral },
    ]

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.surface }]}>
            <View style={styles.topRow}>
                <Pressable
                    onPress={() => setLegendVisible(true)}
                    accessibilityRole="button"
                    accessibilityLabel="Show monitor legend"
                    hitSlop={10}
                    style={({ pressed }) => [
                        styles.legendButton,
                        {
                            borderColor: semantic.divider,
                            backgroundColor: pressed ? theme.colors.surfaceVariant : 'transparent',
                        },
                    ]}
                >
                    <Icon
                        source="information-outline"
                        size={16}
                        color={theme.colors.onSurfaceVariant}
                    />
                    <Text
                        variant="labelSmall"
                        style={{
                            color: theme.colors.onSurfaceVariant,
                            fontWeight: '600',
                            letterSpacing: 0.4,
                        }}
                    >
                        Legend
                    </Text>
                </Pressable>
                <View style={styles.spacer} />
                <MonitorMomentPicker />
            </View>
            <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.chipsRow}
            >
                {chips.map(chip => {
                    const isActive = statusFilter === chip.key
                    return (
                        <Pressable
                            key={chip.key}
                            onPress={() => setStatusFilter(isActive ? null : chip.key)}
                            style={[
                                styles.chip,
                                {
                                    backgroundColor: isActive
                                        ? `${chip.color}33`
                                        : theme.colors.surfaceVariant,
                                    borderColor: isActive ? chip.color : 'transparent',
                                },
                            ]}
                        >
                            <View style={[styles.chipDot, { backgroundColor: chip.color }]} />
                            <Text
                                variant="labelMedium"
                                style={{
                                    color: theme.colors.onSurface,
                                    fontWeight: isActive ? '600' : '400',
                                }}
                            >
                                {chip.count} {chip.label}
                            </Text>
                        </Pressable>
                    )
                })}
                {statusFilter ? (
                    <Pressable
                        onPress={() => setStatusFilter(null)}
                        style={[styles.clearChip, { backgroundColor: theme.colors.surfaceVariant }]}
                    >
                        <Text variant="labelMedium" style={{ color: theme.colors.onSurface }}>
                            Clear filter
                        </Text>
                    </Pressable>
                ) : null}
            </ScrollView>
            <MonitorLegend visible={legendVisible} onDismiss={() => setLegendVisible(false)} />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 10,
    },
    topRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginBottom: 8,
    },
    spacer: {
        flex: 1,
    },
    legendButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 14,
        borderWidth: StyleSheet.hairlineWidth,
    },
    chipsRow: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
        justifyContent: 'center',
        flexGrow: 1,
    },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 16,
        borderWidth: 1,
    },
    chipDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
    },
    clearChip: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 16,
    },
})
